// Global axios interceptor for cookie-based session refresh + human-readable error surfacing.
// Import once (in App.js) — attaches to the default axios instance.
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let refreshPromise = null;

// Extract a human-readable error message from an axios error
export const getErrorMessage = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join('; ');
  if (err?.message) return err.message;
  return fallback;
};

// Attach the interceptor exactly once
let installed = false;
export const installAxiosRefresh = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config || {};
      // Only auto-refresh on 401 for our own backend calls that used credentials
      const is401 = error.response?.status === 401;
      const url = original.url || '';
      const isOurAPI = url.startsWith(API_URL) || url.startsWith('/api');
      const isRefreshCall = url.includes('/api/auth/refresh');
      const isLoginCall = url.includes('/api/auth/login') || url.includes('/api/auth/register');

      if (!is401 || !isOurAPI || isRefreshCall || isLoginCall || original._retried) {
        return Promise.reject(error);
      }

      original._retried = true;
      try {
        // De-dupe simultaneous refreshes
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        // Retry the original request with credentials
        return axios({ ...original, withCredentials: true });
      } catch (_e) {
        // Refresh failed — surface original 401
        return Promise.reject(error);
      }
    }
  );
};
